"use client"

import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react"
import * as React from "react"

import { cn } from "../../lib/utils"

type CarouselApi = UseEmblaCarouselType[1]
type CarouselProps = React.HTMLAttributes<HTMLDivElement> & {
  opts?: Parameters<typeof useEmblaCarousel>[0]
  plugins?: any[]
}

const CarouselContext = React.createContext<{
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: CarouselApi | undefined
} | null>(null)

export function useCarousel() {
  const context = React.useContext(CarouselContext)
  if (!context) throw new Error("useCarousel must be used within <Carousel>")
  return context
}

export const Carousel = React.forwardRef<HTMLDivElement, CarouselProps>(
  ({ className, opts, plugins, children, ...props }, ref) => {
    const [carouselRef, api] = useEmblaCarousel(opts, plugins)
    return (
      <CarouselContext.Provider value={{ carouselRef, api }}>
        <div ref={ref} className={cn("relative", className)} {...props}>
          <div className="overflow-hidden" ref={carouselRef}>
            {children}
          </div>
        </div>
      </CarouselContext.Provider>
    )
  }
)
Carousel.displayName = "Carousel"

export const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("-ml-4 flex", className)} {...props} />
))
CarouselContent.displayName = "CarouselContent"

export const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("pl-4 min-w-0 flex-[0_0_100%]", className)} {...props} />
))
CarouselItem.displayName = "CarouselItem"

export const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  const { api } = useCarousel()
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => api?.scrollPrev()}
      className={cn(
        "absolute left-2 top-1/2 -translate-y-1/2 z-10 rounded border bg-white/80 px-2 py-1 shadow",
        className
      )}
      {...props}
    >
      ‹
    </button>
  )
})
CarouselPrevious.displayName = "CarouselPrevious"

export const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  const { api } = useCarousel()
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => api?.scrollNext()}
      className={cn(
        "absolute right-2 top-1/2 -translate-y-1/2 z-10 rounded border bg-white/80 px-2 py-1 shadow",
        className
      )}
      {...props}
    >
      ›
    </button>
  )
})
CarouselNext.displayName = "CarouselNext"


